import { useState, useEffect } from 'react'
import { format, isValid, parseISO } from 'date-fns'
import { DayPicker, DateRange, SelectRangeEventHandler } from 'react-day-picker'
import 'react-day-picker/dist/style.css'
import { useRouter } from 'next/router'

import { useMarket } from '@/utils/multi-markets/context'
import { getDateFnsLocale } from '@/utils/dateFormatters'

import { DatePickerWrapperStyles } from './DatePickerWrapperStyles'

interface DatePickerProps {
  value: string
  setSettedStartDate: (value: string) => void
  setSettedEndDate: (value: string) => void
  label: string
  placeholder?: string
  isReservationComponent: boolean
  settedStartDate: string
  settedEndDate: string
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  setSettedStartDate,
  setSettedEndDate,
  settedStartDate,
  settedEndDate
}) => {
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    () => {
      if (settedStartDate && settedEndDate) {
        const fromDate = parseISO(settedStartDate)
        const toDate = parseISO(settedEndDate)

        if (isValid(fromDate) && isValid(toDate)) {
          return { from: fromDate, to: toDate }
        }
      }
      return undefined
    }
  )

  const { locale } = useMarket()
  const router = useRouter()

  const handleRangeSelect: SelectRangeEventHandler = range => {
    if (!range?.from) return
    if (selectedRange?.from && selectedRange?.to) {
      let newStartDate
      if (selectedRange?.from !== range?.from) {
        newStartDate = format(range?.from, 'yyyy-MM-dd')
        setSettedStartDate(newStartDate)
        setSettedEndDate('')
        setSelectedRange({ from: range.from, to: undefined })
      } else if (selectedRange?.to !== range?.to) {
        newStartDate = range.to ? format(range.to, 'yyyy-MM-dd') : ''
        setSettedStartDate(newStartDate)
        setSettedEndDate('')
        setSelectedRange({ from: range.to, to: undefined })
      }

      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            startDate: newStartDate,
            endDate: undefined
          }
        },
        undefined,
        { shallow: true }
      )
      return
    }

    let startDate = settedStartDate
    let endDate = settedEndDate

    if (!settedStartDate && range?.from) {
      startDate = format(range.from, 'yyyy-MM-dd')
      setSettedStartDate(startDate)
      setSettedEndDate('')
    }

    if (!settedEndDate && range?.to) {
      startDate = format(range.from, 'yyyy-MM-dd')
      endDate = format(range.to, 'yyyy-MM-dd')
      setSettedEndDate(endDate)
    }

    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          startDate: startDate,
          ...(endDate && { endDate })
        }
      },
      undefined,
      { shallow: true }
    )

    setSelectedRange(range)
  }

  useEffect(() => {
    if (value && !selectedRange) {
      const [from, to] = value.split(' - ')
      const fromDate = new Date(from)
      const toDate = new Date(to)

      if (isValid(fromDate) && isValid(toDate)) {
        setSelectedRange({
          from: fromDate,
          to: toDate
        })
      }
    }
  }, [value, selectedRange])

  return (
    <DatePickerWrapperStyles noMargin={false}>
      <DayPicker
        mode="range"
        selected={selectedRange}
        onSelect={handleRangeSelect}
        locale={getDateFnsLocale(locale)}
        disabled={{ before: new Date() }}
      />
    </DatePickerWrapperStyles>
  )
}

export default DatePicker
